import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { enqueueSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { Card, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import { getAPICall, putAPICall } from "lib/callApi";
import { GET_HUB_SETTINGS, PERMISSIONS_NAME } from "lib/constants";
import Loader from "utils/Loader";
import { checkPermission } from "utils/HelperFunctions";
import InsightsForm from "./Insights/InsightsForm";
import { generateChangelogHTML } from "./Insights/changelog";

const InsightsSettings = ({ selectedInsightsConfigType }) => {
  const user = useSelector((state) => state.loginUser.loginUserState);
  const [pageLoading, setPageLoading] = useState(true);
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);

  const fetchInsightsSettings = async () => {
    setPageLoading(true);
    try {
      const query = new URLSearchParams({
        type: selectedInsightsConfigType,
      }).toString();

      const fetchedSettings = await getAPICall(`${GET_HUB_SETTINGS}/Insights?${query}`, true);
      if (fetchedSettings?.data?.success) {
        setSettings(fetchedSettings?.data?.settings || {});
      } else {
        setSettings({});
        if (fetchedSettings.response.status !== 403) {
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: fetchedSettings.response.data.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
        }
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: err.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setPageLoading(false);
    }
  };

  const onSubmit = async (data, skipUserCompanyConfirmation = false) => {
    const changeLogHTML = await generateChangelogHTML(settings, data);

    try {
      const hasManageInsightsPermission = checkPermission(
        PERMISSIONS_NAME.MANAGE_INSIGHTS,
        user.userPermission
      );

      let saveUserSettings = true;
      let dismissed = false;

      if (hasManageInsightsPermission && !skipUserCompanyConfirmation) {
        await Swal.fire({
          title: "Save Configuration",
          icon: "warning",
          showDenyButton: true,
          showCancelButton: true,
          confirmButtonText: "Save as company default",
          denyButtonText: `Save only for me`,
          reverseButtons: true,
          html: `
            <div style="text-align: start; max-height: 60vh; overflow-y: auto;">
              <p>You can save these settings either for <b>your own preferences</b> or as the <b>company default</b>.</p>
              <ul style="margin: 20px;">
                <li><b>Your Settings</b> : Changes will apply only to you.</li>
                <li><b>Company Default</b> : Changes will apply to all users as the default settings.</li>
              </ul>  
              <p>Users with the <b>Manage Insights</b> permission enabled can also <b>override the company default settings</b>.</p>

              <div style="margin-top:30px;">
                ${changeLogHTML}
              </div>
            </div>
          `,
          width: "700px",
        }).then(async ({ isConfirmed, isDenied, isDismissed = false }) => {
          if (isDismissed) {
            dismissed = true;
          } else if (isConfirmed || isDenied) {
            saveUserSettings = !isConfirmed;
          }
        });
      } else if (skipUserCompanyConfirmation) {
        saveUserSettings = selectedInsightsConfigType === "user";
      }
      if (dismissed) return;

      setSaving(true);
      const result = await putAPICall(`${GET_HUB_SETTINGS}/Insights`, true, {
        insights: data,
        type: saveUserSettings ? "user" : "company",
      });
      if (result.status === 200) {
        setShowSnackbar(true);
        setHasChanges(false);
        fetchInsightsSettings();
      } else {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text:
              result?.response?.data?.errorMessage ||
              "Something went wrong! Development has been notified.",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: err?.response?.data?.message || err?.message || "Something went wrong",
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setSaving(false);
    }
  };

  const closeSnackbar = () => setShowSnackbar(false);

  useEffect(() => {
    fetchInsightsSettings();
  }, [selectedInsightsConfigType]);

  return (
    <Grid item xs={12}>
      <Loader loading={pageLoading} />
      <Card>
        <MDBox p={3}>
          <MDTypography variant="h4">Insights</MDTypography>
          <MDTypography variant="body2">
            Insights alerts you when your team is above or below a specific percentage threshold on
            a KPI, measured against the trailing average. This helps you quickly identify trends and
            take action to keep performance on track.
          </MDTypography>
        </MDBox>
      </Card>
      <MDBox my={3}>
        {!pageLoading ? (
          <InsightsForm
            settings={settings}
            saving={saving}
            hasChanges={hasChanges}
            onSubmit={onSubmit}
            setHasChanges={setHasChanges}
            selectedInsightsConfigType={selectedInsightsConfigType}
          />
        ) : null}
      </MDBox>
      <MDSnackbar
        color="success"
        icon="checkCircleIcon"
        title="Success"
        content="Insights settings saved successfully"
        open={showSnackbar}
        onClose={closeSnackbar}
        close={closeSnackbar}
        bgWhite
      />
    </Grid>
  );
};

export default InsightsSettings;
